import { type ReactNode, useEffect, useRef, useState } from "react";
import { Animated, View } from "react-native";

import { useAppTheme } from "@hooks/useAppTheme";
import usePlayer from "@memorang/features/src/audio/usePlayer";
import { WaveformVisualizer } from "@memorang/ui/src/components/WaveformVisualizer";
import { IconButton, Text } from "react-native-paper";

import { base64ToMp3DataUri } from "../helpers/audio";
import { useActiveScenario } from "../hooks/useActiveScenario";
import { useProethicsFlowStore } from "../hooks/useProethicsFlowStore";
import type { TranscriptMessage } from "../types";
import { PersonaAvatar } from "./PersonaAvatar";

// The reply audio is a base64 data URI, so the waveform-data endpoint can't
// analyze it; a local pattern via meterHistory avoids that fetch entirely.
// Dense sampling (160 points) survives WaveformVisualizer's interpolation;
// the sin(i * 12.9898) term adds deterministic speech-like jitter.
const WAVE_PATTERN = Array.from({ length: 160 }, (_, i) => {
	const t = i / 160;
	const wave =
		Math.sin(t * Math.PI * 6) * 0.35 +
		Math.sin(t * Math.PI * 13) * 0.25 +
		Math.sin(t * Math.PI * 29 + 1.3) * 0.2 +
		Math.sin(i * 12.9898) * 0.2;
	return 0.12 + 0.88 * Math.abs(wave);
});

const formatTime = (ms: number) => {
	const total = Math.max(0, Math.round(ms / 1000));
	return `0:${String(total % 60).padStart(2, "0")}`;
};

const AudioRow = ({ children }: { children: ReactNode }) => {
	const theme = useAppTheme();
	return (
		<View
			style={{
				flexDirection: "row",
				alignItems: "center",
				gap: 10,
				marginTop: 10,
				paddingTop: 10,
				borderTopWidth: 1,
				borderTopColor: theme.colors.outlineVariant,
			}}
		>
			{children}
		</View>
	);
};

// Audio arrives in a separate SSE event after the reply text; the row mounts
// only then, and the pinned thread auto-scroll keeps the bubble in view.
// Autoplay only the just-streamed reply: the whole transcript re-mounts when
// the learner navigates back to the item, and every row autoplaying at once
// is the alternative.
const DariaAudio = ({
	base64,
	messageId,
}: {
	base64: string;
	messageId: string;
}) => {
	const theme = useAppTheme();
	const uri = base64ToMp3DataUri(base64);
	const [autoPlay] = useState(
		() => useProethicsFlowStore.getState().autoplayMessageId === messageId,
	);
	useEffect(() => {
		if (autoPlay) {
			useProethicsFlowStore.setState({ autoplayMessageId: null });
		}
	}, [autoPlay]);
	const player = usePlayer(uri, undefined, autoPlay);
	const total = player.totalTimeInMilliseconds || 0;
	const progress =
		total > 0 ? Math.min(1, player.currentTimeInMilliseconds / total) : 0;
	const ended = !player.isPlaying && player.isEnded;

	const icon = player.isPlaying ? "pause" : ended ? "replay" : "play";
	const label = player.isPlaying
		? "Pause reply"
		: ended
			? "Replay reply"
			: "Play reply";

	return (
		<AudioRow>
			<IconButton
				icon={icon}
				size={16}
				mode="contained"
				containerColor={theme.colors.primary}
				iconColor={theme.colors.onPrimary}
				style={{ margin: 0 }}
				accessibilityLabel={label}
				onPress={() => {
					if (player.isPlaying) {
						player.pauseSound();
						return;
					}
					if (ended) {
						player.onSeek(0);
					}
					player.playSound();
				}}
			/>
			<WaveformVisualizer
				progress={progress}
				isPlaying={player.isPlaying}
				meterHistory={WAVE_PATTERN}
				barWidth={6}
				barMargin={1}
				minHeight={3}
				maxHeight={18}
				activeColor={theme.colors.tertiary}
				inactiveColor={theme.colors.outlineVariant}
				onSeek={(p) => player.onSeek(p * total)}
			/>
			<Text
				variant="labelSmall"
				style={{
					color: theme.colors.onSurfaceVariant,
					minWidth: 30,
					textAlign: "right",
				}}
			>
				{formatTime(
					player.isPlaying ? player.currentTimeInMilliseconds : total,
				)}
			</Text>
		</AudioRow>
	);
};

const TypingDots = () => {
	const theme = useAppTheme();
	const { scenario } = useActiveScenario();
	const dotsRef = useRef([
		new Animated.Value(0.3),
		new Animated.Value(0.3),
		new Animated.Value(0.3),
	]);

	useEffect(() => {
		const animations = dotsRef.current.map((dot, i) =>
			Animated.loop(
				Animated.sequence([
					Animated.delay(i * 160),
					Animated.timing(dot, {
						toValue: 1,
						duration: 300,
						useNativeDriver: true,
					}),
					Animated.timing(dot, {
						toValue: 0.3,
						duration: 300,
						useNativeDriver: true,
					}),
					Animated.delay((2 - i) * 160),
				]),
			),
		);
		for (const animation of animations) {
			animation.start();
		}
		return () => {
			for (const animation of animations) {
				animation.stop();
			}
		};
	}, []);

	return (
		<View
			accessibilityLabel={`${scenario.persona.name} is typing`}
			style={{
				flexDirection: "row",
				alignItems: "center",
				gap: 4,
				minHeight: 22,
			}}
		>
			{dotsRef.current.map((dot, i) => (
				<Animated.View
					// biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static list
					key={i}
					style={{
						width: 7,
						height: 7,
						borderRadius: 4,
						backgroundColor: theme.colors.onSurfaceVariant,
						opacity: dot,
					}}
				/>
			))}
		</View>
	);
};

export const MessageBubble = ({
	message,
	isStreaming,
}: {
	message: TranscriptMessage;
	isStreaming?: boolean;
}) => {
	const theme = useAppTheme();
	if (message.role === "mentor") {
		return (
			<View
				style={{
					flexDirection: "row",
					justifyContent: "flex-end",
					alignItems: "flex-start",
					marginVertical: 6,
				}}
			>
				<View
					style={{
						flexShrink: 1,
						maxWidth: "80%",
						backgroundColor: theme.colors.primary,
						paddingVertical: 12,
						paddingHorizontal: 16,
						borderRadius: theme.roundness * 4,
						borderBottomRightRadius: theme.roundness,
					}}
				>
					<Text variant="bodyLarge" style={{ color: theme.colors.onPrimary }}>
						{message.text}
					</Text>
				</View>
			</View>
		);
	}

	return (
		<View
			style={{
				flexDirection: "row",
				gap: 10,
				alignItems: "flex-start",
				marginVertical: 6,
			}}
		>
			<PersonaAvatar size={32} />
			<View
				style={{
					flexShrink: 1,
					maxWidth: "80%",
					backgroundColor: theme.colors.surfaceVariant,
					borderWidth: 1,
					borderColor: theme.colors.outlineVariant,
					padding: 14,
					borderRadius: theme.roundness * 4,
					borderBottomLeftRadius: theme.roundness,
				}}
			>
				{message.text ? (
					<Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
						{message.text}
					</Text>
				) : isStreaming ? (
					<TypingDots />
				) : (
					<Text
						variant="bodyLarge"
						style={{ color: theme.colors.onSurfaceVariant }}
					>
						…
					</Text>
				)}
				{message.audioBase64 ? (
					<DariaAudio base64={message.audioBase64} messageId={message.id} />
				) : null}
			</View>
		</View>
	);
};
